import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Input,
  Select,
  Option,
  Alert,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/joy';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { tagService } from '../services/tagService';
import { Tag, TagWithCreator, CreateTagData, UpdateTagData } from '../types/tag';
import { TagForm } from '../components/TagForm';
import { TagChip } from '../components/TagChip';
import { useAuth } from '../context/AuthContext';

export const TagManagementPage: React.FC = () => {
  const { user, permissions } = useAuth();
  const [tags, setTags] = useState<TagWithCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState<TagWithCreator | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: any = { is_active: true };
      if (searchTerm) filters.search = searchTerm;
      if (categoryFilter) filters.category = categoryFilter;
      
              const loadedTags = await tagService.getAllTags(filters);
      setTags(loadedTags);
    } catch (error) {
      console.error('Error loading tags:', error);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async (data: CreateTagData) => {
    try {
      setFormLoading(true);
      await tagService.createTag(data);
      await loadTags();
      setShowForm(false);
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTag = async (data: UpdateTagData) => {
    if (!editingTag) return;
    
    try {
      setFormLoading(true);
      await tagService.updateTag(editingTag.id, data);
      await loadTags();
      setEditingTag(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    if (!confirm('Are you sure you want to delete this tag?')) return;
    
    try {
      await tagService.deleteTag(tagId);
      await loadTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      setError('Failed to delete tag');
    }
  };

  const handleEditTag = (tag: Tag) => {
    setEditingTag(tag);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: CreateTagData | UpdateTagData) => {
    if (editingTag) {
      await handleUpdateTag(data as UpdateTagData);
    } else {
      await handleCreateTag(data as CreateTagData);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTag(null);
  };

  const categories = Array.from(new Set(tags.map(tag => tag.category).filter(Boolean)));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography level="h3">Tag Management</Typography>
        <Button
          startDecorator={<Add />}
          onClick={() => setShowForm(true)}
          disabled={!permissions.includes('tags.create')}
        >
          Create Tag
        </Button>
      </Stack>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Input
                placeholder="Search tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                startDecorator={<Search />}
                onKeyPress={(e) => e.key === 'Enter' && loadTags()}
              />
            </Box>
            <Select
              value={categoryFilter}
              onChange={(_, value) => setCategoryFilter(value || '')}
              placeholder="All Categories"
              sx={{ minWidth: 150 }}
            >
              <Option value="">All Categories</Option>
              {categories.map((category) => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
            <Button onClick={loadTags}>Filter</Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Tags Grid */}
      <Grid container spacing={2}>
        {tags.map((tag) => (
          <Grid xs={12} sm={6} md={4} lg={3} key={tag.id}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <TagChip tag={tag} size="md" />
                    <Stack direction="row" spacing={0.5}>
                      {permissions.includes('tags.edit') && (
                        <IconButton
                          size="sm"
                          variant="outlined"
                          onClick={() => handleEditTag(tag)}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {permissions.includes('tags.delete') && (
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="danger"
                          onClick={() => handleDeleteTag(tag.id)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                  
                  {tag.description && (
                    <Typography level="body-sm" color="neutral">
                      {tag.description}
                    </Typography>
                  )}
                  
                  <Stack direction="row" spacing={1} alignItems="center">
                    {tag.category && (
                      <Chip size="sm" variant="outlined">
                        {tag.category}
                      </Chip>
                    )}
                    <Typography level="body-xs" color="neutral">
                      Created by {tag.creator?.first_name} {tag.creator?.last_name}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {tags.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography level="body-lg" color="neutral">
            No tags found
          </Typography>
        </Box>
      )}

      {/* Tag Form Modal */}
      <TagForm
        open={showForm}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        tag={editingTag}
        loading={formLoading}
      />
    </Box>
  );
}; 